// React
import { ChangeEventHandler, KeyboardEventHandler, useEffect, useState } from "react";
import clsx from "clsx";

// Styles
import "@/assets/styles/layouts/Text.css";
import "./styles.css";

// MUI
import { TextField, Tooltip } from "@mui/material";

// Helpers
import {
	countDecimalPlaces,
	getDecimalsPlaceHolder,
	getDecimalPart,
	getIndex,
	getSectionedValue,
	hasLeadingZeroes,
	isDeltaAdmissible,
	isValidCharacter,
	getTimeDuration,
} from "./inputUtils";
import { isNumeric } from "@/Common/helpers/Verifications/numbers";
import { getFormatAmount, unformattedAmount } from "@/Common/helpers/number";

// Components
import EndAdornment from "./EndAdornment";
import StartAdornment from "./StartAdornment";
import { Nullable } from "@/Common/interfaces/types";
import { DefinitionState, GetInputValueByTypeParams, IInputValueByTypeReturn, InputValueProps, UpdateValueHandlerParams } from "./types";
import { DataRecord } from "../types";

const isConditionalSwitchable = (type: string) => ["DURATION", "UNIT"].includes(type);

const isHideStartButton = (element: Nullable<DataRecord>, isSwitchable = false) => {
	if (!element) {
		return true;
	}
	const { type } = element || {};
	const upperType = type?.toUpperCase();
	const isUnacceptableType = upperType === "ORDINAL";
	const unSwitchableInputType = isConditionalSwitchable(upperType) && !isSwitchable;
	return isUnacceptableType || unSwitchableInputType;
};

const isHideEndButton = (element: Nullable<DataRecord>) => {
	if (!element) {
		return true;
	}
	const { type, decimalsEditable } = element || {};
	const upperType = type?.toUpperCase();
	const isUnacceptableTypes = upperType === "UNIT" || upperType === "DURATION";
	return decimalsEditable || isUnacceptableTypes;
};

const getProperMinMax = (definition: Nullable<DataRecord>) => {
	if (!definition) {
		return {
			min: Number.MIN_SAFE_INTEGER,
			max: Number.MAX_SAFE_INTEGER,
		};
	}

	let minNumber = Number.MIN_SAFE_INTEGER;
	let maxNumber = Number.MAX_SAFE_INTEGER;

	if (definition.min !== null && definition.min !== undefined) {
		minNumber = definition.min;
	}
	if (definition.max !== null && definition.max !== undefined) {
		maxNumber = definition.max;
	}

	return {
		min: minNumber,
		max: maxNumber,
	};
};

const isDecimalsAllowed = (value: number, definition: Nullable<DataRecord>) => {
	const { decimals, decimalsEditable } = definition || {};
	const decimalPlaces = countDecimalPlaces(value);
	if (!decimals || !decimalsEditable) {
		return decimalPlaces <= 0;
	}
	return decimalPlaces <= decimals;
};

const isInputDisabled = (element: Nullable<DataRecord>) => Boolean(element?.disabled ?? element?.readOnly);

const isConditionsCompliant = (updateValue: string, definition: Nullable<DataRecord>, isDeleting = false) => {
	const { min, max } = getProperMinMax(definition);

	// Checking applicable conditions
	const isAllowedDecimals = isDecimalsAllowed(Number(updateValue), definition);

	const isInRange = Number(updateValue) >= min && Number(updateValue) <= max;
	const leadingZeroes = hasLeadingZeroes(updateValue);
	const admissibleDelta = isDeltaAdmissible(Number(updateValue), definition?.decimals, definition?.delta);
	const mandatoryConditions = isAllowedDecimals && isInRange && admissibleDelta && !leadingZeroes;

	return isDeleting || (isNumeric(updateValue) && mandatoryConditions);
};

const sanitizeValue = (value = 0) => (Number(value) === -Number(0) ? Math.abs(Number(value)) : value);

const getDefaultValue = (definition: Nullable<DataRecord>, label = "Default", isMandatoryDefault = false) => {
	if (!definition) {
		return "";
	}
	return definition?.defaultValue || isMandatoryDefault ? (definition?.defaultLabel ?? label) : "";
};

const isEmptyOriginal = (original = "") => Boolean(!original);
const isInputDuration = (type = "") => type?.toUpperCase().startsWith("DURATION");
const isInfinityBound = (value: string) => value === "Infinity" || value === "-Infinity";
const isAtomicAllowed = ({ enterValue, inputDefinition }: { enterValue: string; inputDefinition: Nullable<DataRecord> }) => {
	if (!enterValue) {
		return true;
	}
	const { min = 0, decimalsEditable = false } = inputDefinition || {};
	const isAllowedDot = enterValue === "." && decimalsEditable;
	const isAllowedNegative = enterValue === "-" && min < 0;
	return isAllowedDot || isAllowedNegative;
};
const isInvalidCondition = ({
	key,
	eventValue,
	min,
	decimalsEditable,
}: {
	key: string;
	eventValue: string;
	min: number;
	decimalsEditable: Nullable<boolean>;
}) => {
	const minCheck = min >= 0 && key === "-";
	const decimalsCheck = !decimalsEditable && key === ".";
	const repeatedDots = key === "." && eventValue.includes(".");
	return minCheck || decimalsCheck || repeatedDots;
};

const isSpecialNegative = (value: string) => value === "-" || value === "-0";

const isValidEnd = (value: string) => value.endsWith(".") || value.endsWith(".0") || value.endsWith("-");

const getProperValue = ({
	original,
	inputValue,
	entireValue,
	decimalsEditable = false,
}: { entireValue: string | number; decimalsEditable: boolean } & Partial<DefinitionState>) => {
	let showValue = original;
	if (String(original).includes(".")) {
		const decimalPart = String(original).split(".")[1];
		if (decimalPart && decimalPart !== "0" && !Number(decimalPart)) {
			showValue = inputValue;
		}
	}
	if (!decimalsEditable) {
		showValue = entireValue;
	}
	return showValue;
};

const getProperValueToShow = (
	definition: { entireValue: string | number; originalValue: string | number | undefined; formatted: boolean } & Partial<DefinitionState>,
	isEditing = false,
	decimalsEditable = false,
	decimals = 0
) => {
	const { original, inputValue, entireValue, originalValue, formatted } = definition;

	if (original === "-") {
		return "-";
	}

	if (isEmptyOriginal(String(originalValue))) {
		return "";
	}

	if (!isEditing) {
		if (!decimalsEditable) {
			return getFormatAmount(entireValue, 0);
		}

		if (decimalsEditable && isNumeric(original) && isNumeric(formatted)) {
			return Number(original).toFixed(decimals);
		}

		return formatted;
	}

	if (decimalsEditable && isValidEnd(String(originalValue))) {
		return originalValue;
	}

	return getProperValue({
		original,
		inputValue,
		entireValue,
		decimalsEditable,
	});
};

const getShowValue = (args: { value: string; definition: DefinitionState; isDashed: boolean; defaultValue: boolean }) => {
	const { value, definition, isDashed = false, defaultValue = false } = args || {};

	if (isDashed) {
		return "-";
	}
	const { original, edited } = definition || {};
	const isEmptyShow = !original && !edited && !defaultValue;

	if (isEmptyShow) {
		return "";
	}
	return `${value}`;
};

const getInputValueByType = ({
	enterValue,
	formattedValue,
	referenceValue,
	isDuration = false,
}: GetInputValueByTypeParams): IInputValueByTypeReturn => {
	if (isSpecialNegative(enterValue)) {
		return {
			inputValue: enterValue,
			currentValue: enterValue,
		};
	}

	return {
		inputValue: isDuration ? enterValue : formattedValue,
		currentValue: isDuration ? enterValue : referenceValue,
	};
};

const updateValueHandler = ({ enterValue, nativeEvent, inputDefinition, setEditing, isToSend = false }: UpdateValueHandlerParams) => {
	const isDuration = isInputDuration(inputDefinition?.type);
	let updatedValue = unformattedAmount(enterValue);

	const exact = inputDefinition?.exact ?? false;
	const { duration, referenceTime, isTimeFormat } = getTimeDuration(enterValue, exact);

	if (isDuration) {
		updatedValue = isTimeFormat ? duration : referenceTime;
	}

	setEditing?.(!isToSend);
	if (isInfinityBound(updatedValue)) {
		return {
			updatedValue: "invalid",
		};
	}

	const { inputType } = nativeEvent || {};
	const isDeleting = inputType === "deleteContentBackward";

	const { decimals } = inputDefinition || {};

	const decimalPart = getDecimalPart(Number(enterValue), decimals);
	const valueDefinition = getSectionedValue(updatedValue, Number(decimalPart), decimals ?? 0);

	const isCompliant = isConditionsCompliant(updatedValue, inputDefinition, isDeleting);
	const isAtomic = isAtomicAllowed({ enterValue, inputDefinition });
	if (!isAtomic && !isCompliant) {
		return {
			updatedValue: "invalid",
		};
	}

	const { referenceValue, decimalValue, formattedValue } = valueDefinition || {};
	const { inputValue, currentValue } = getInputValueByType({ enterValue, formattedValue, referenceValue, isDuration });

	return {
		updatedValue,
		inputValue,
		currentValue,
		decimalValue,
	};
};

const getPropagationValue = (value?: string | number) => {
	const toZeroedValue = value === "-" || value === ".";
	const toZeroValueConditional = ["-0", "0.", ".0"].some((item) => value === item);
	const unformatted = unformattedAmount(String(value));
	const isNaNValue = String(Number(unformatted)) === "NaN";
	if (isEmptyOriginal(String(value)) || toZeroedValue || toZeroValueConditional || isNaNValue) {
		return 0;
	}
	return Number(unformatted);
};

const isDisabled = (readOnly: Nullable<boolean>, disabled: Nullable<boolean>, variant = "") =>
	readOnly || disabled || variant === "disabled";
const inputRelated = (related: Nullable<boolean>, variant = "") => related && variant !== "standard";
const inputError = (error: Nullable<boolean>, variant = "") => (error && variant !== "standard") || variant === "error";

function isSwitchableFn(data?: DataRecord[]) {
	return data?.length && data.length > 1;
}

function guessType(type?: string) {
	return type ?? "Unit";
}

function isDurationFn(type?: string) {
	return type?.toUpperCase().startsWith("DURATION");
}

function guessPlaceholder(placeholder?: string, data?: DataRecord[], index = 0, decimalsEditable = false) {
	const dataIndex = Math.min(data?.length ?? 0, 1000);
	const indexToUse = dataIndex >= index ? index : 0;
	return placeholder ?? getDecimalsPlaceHolder(data?.[indexToUse]?.decimals, isInputDuration(data?.[indexToUse]?.type), decimalsEditable);
}

const isPropagable = (isValid: boolean, value?: number | string, inputValue?: number) => isValid && Number(value) !== Number(inputValue);

function numberOrZero(value: Nullable<string | number>, decimals?: number) {
	if (!value) {
		return 0;
	}
	return Number(value).toFixed(decimals ?? 0) ?? 0;
}

function checkInputDisabled(readOnly = false, disabled = false, element?: DataRecord, variant?: string) {
	return (isDisabled(Boolean(readOnly), Boolean(disabled), variant) || isInputDisabled(element)) && variant !== "standard";
}

export default function InputValue(props: Readonly<InputValueProps>) {
	const {
		data,
		value,
		defaultType,
		defaultValue,
		defaultLabel,
		placeholder,
		variant,
		classes,
		error,
		related,
		disabled,
		readOnly,
		isEditingMode,
		sx,
		onActionUpdate,
		onBlur,
		onChangeType,
		onChange,
		onClickInput,
		onFocusOut,
	} = props;

	const [index, setIndex] = useState(0);
	const [editing, setEditing] = useState(false);
	const [definition, setDefinition] = useState<DefinitionState>({
		value: 0,
		inputValue: 0,
		decimalSection: 0,
		original: "",
		type: defaultType ?? "Unit",
		edited: false,
	});

	const isSwitchable = isSwitchableFn(data);
	const isDashed = data?.[index]?.dashed;
	const decimalsEditable = data?.[index]?.decimalsEditable;
	const { min } = getProperMinMax(data?.[index]);

	const propagableNumber = (valueToPropagate: unknown, decimals = 0) => Number(valueToPropagate).toFixed(decimals);

	// Fn Controllers
	const propagateUpdateAction = (inputValue = 0, decimals = 0, isValid = false) => {
		if (isPropagable(isValid, value, inputValue)) {
			onActionUpdate?.(propagableNumber(inputValue, decimals), definition.type);
		}
	};

	const preventKeysBehaviors: KeyboardEventHandler<HTMLInputElement> = (e) => {
		const target = e.target as HTMLInputElement;
		const eventValue = target.value;

		const isPreventedConditions = isInvalidCondition({ key: e.key, eventValue, min, decimalsEditable });

		setEditing(true);

		if (e.key === "Enter") {
			const { updatedValue, inputValue } = updateValueHandler({
				enterValue: target.value,
				nativeEvent: e.nativeEvent,
				inputDefinition: data?.[index],
				isToSend: true,
				setEditing,
			});
			const isValid = updatedValue !== "invalid";
			const { decimals } = data?.[index] ?? {};
			propagateUpdateAction(getPropagationValue(inputValue), decimals, isValid);
		}

		if (!isValidCharacter(e.key, e.nativeEvent) || isPreventedConditions) {
			e.preventDefault();
		}
	};

	// Fn Handlers
	const handleChange: ChangeEventHandler<HTMLInputElement> = (e) => {
		const { updatedValue, inputValue, currentValue, decimalValue } = updateValueHandler({
			enterValue: e.target.value,
			nativeEvent: e.nativeEvent,
			inputDefinition: data?.[index],
			isToSend: false,
			setEditing,
		});

		if (updatedValue !== "invalid") {
			setDefinition({
				...definition,
				inputValue,
				value: currentValue,
				original: e.target.value,
				decimalSection: Number(decimalValue),
				edited: true,
			});
			onChange?.(Number(updatedValue), definition.type);
		}
	};

	const handleChangeType = () => {
		let newIndex = index + 1;
		if (newIndex >= Number(numberOrZero(data?.length))) {
			newIndex = 0;
		}

		const { type, decimals } = data?.[newIndex] ?? {};

		const newValue = getSectionedValue(0, Number(getDecimalsPlaceHolder(Number(decimals))), decimals);
		const { inputValue, formattedValue, decimalValue } = newValue ?? {};
		setDefinition({
			type: guessType(type),
			value: inputValue,
			inputValue: formattedValue,
			decimalSection: decimalValue,
			edited: false,
		});
		setIndex(newIndex);
		setEditing(false);
		onChangeType?.(inputValue, guessType(type));
	};

	const handleFocusOut = () => {
		setEditing(false);
		const isDuration = isDurationFn(data?.[index]?.type);

		let updatedValue = !isNumeric(definition.value) ? 0 : sanitizeValue(Number(definition.value));
		const exact = data?.[index]?.exact ?? false;
		const { duration, displayTime } = getTimeDuration(definition.value, exact);
		if (isDuration) {
			updatedValue = Number(duration) ? duration : 0;
		}

		const { decimals } = data?.[index] ?? {};
		const valueDefinition = getSectionedValue(updatedValue, Number(definition.decimalSection ?? 0), decimals);
		const { inputValue, formattedValue, decimalValue } = valueDefinition ?? {};

		const formatted = isDuration ? displayTime : formattedValue;

		setDefinition({
			...definition,
			inputValue: formatted,
			decimalSection: decimalValue,
			value: inputValue,
			original: inputValue,
			edited: false,
		});

		onFocusOut?.(propagableNumber(inputValue, decimals), definition.type);
		onBlur?.(propagableNumber(inputValue, decimals), definition.type);
		propagateUpdateAction(getPropagationValue(String(inputValue)), decimals, true);
	};

	const getShowInputValue = (inputElement: DefinitionState, isEditing = false, inputDefault = "") => {
		if (inputDefault) {
			return inputDefault;
		}

		const { decimals } = data?.[index] ?? {};
		const { value: localValue, decimalSection, edited } = inputElement;
		const showValue = getSectionedValue(localValue, Number(decimalSection), decimals);
		const { entireValue, formattedValue, originalValue, inputValue } = showValue;

		const isDuration = isDurationFn(inputElement?.type);
		const exact = data?.[index]?.exact ?? false;
		const { displayTime, referenceTime } = getTimeDuration(localValue, exact);

		const isEditingInput = isDuration ? isEditing && edited : isEditing;

		return getProperValueToShow(
			{
				inputValue,
				entireValue,
				originalValue: inputElement?.original,
				formatted: isDuration ? displayTime : formattedValue,
				original: isDuration ? referenceTime : originalValue,
			},
			isEditingInput,
			decimalsEditable,
			decimals
		);
	};

	useEffect(() => {
		const defaultIndex = getIndex(data, defaultType);
		const currentIndex = defaultIndex !== -1 ? defaultIndex : 0;

		const typeToAssign = guessType(defaultType ?? data?.[currentIndex].type);

		const decimals = data?.[currentIndex].decimals;
		const decimalPart = getDecimalPart(value as number, decimals);
		const valueDefinition = getSectionedValue(value, Number(decimalPart), decimals);
		const { decimalValue, formattedValue, inputValue } = valueDefinition;

		const isDuration = isDurationFn(data?.[currentIndex]?.type);
		const exact = data?.[currentIndex].exact ?? false;
		const { displayTime } = getTimeDuration(value, exact);
		const formatted = isDuration ? displayTime : formattedValue;

		setIndex(currentIndex);
		setDefinition({
			value: numberOrZero(inputValue, decimals),
			inputValue: formatted,
			original: Number(inputValue) ? Number(inputValue) : "",
			decimalSection: decimalValue,
			type: typeToAssign,
			edited: false,
		});

		setEditing(Boolean(isEditingMode));

		return () => {
			setIndex(0);
			setEditing(false);
			setDefinition({
				value: 0,
				inputValue: 0,
				original: "",
				decimalSection: 0,
				type: "Unit",
				edited: false,
			});
		};
	}, [data, value, defaultType, isEditingMode]);

	// Constants
	const inputDefaultValue = getDefaultValue(data?.[index], defaultLabel, defaultValue);
	const inputDisabled = checkInputDisabled(readOnly, disabled, data?.[index], variant);

	const hideStartButton = isHideStartButton(data?.[index], Boolean(isSwitchable));
	const hideEndButton = isHideEndButton(data?.[index]);
	const showInputValue = getShowInputValue(definition, editing, inputDefaultValue);
	const show = getShowValue({
		definition,
		value: showInputValue,
		isDashed: Boolean(isDashed),
		defaultValue: Boolean(defaultValue),
	});

	return (
		<Tooltip title={props?.tooltipMessage ?? ""} placement={props?.tooltipPlacement ?? "top"}>
			<TextField
				autoComplete={"off"}
				type="text"
				placeholder={guessPlaceholder(placeholder, data, index, decimalsEditable)}
				className={clsx({
					"regular-text-input": true,
					"text-input-error": inputError(error, variant),
					"related-text-input": inputRelated(related, variant),
					"disabled-text-input": inputDisabled,
					"fixed-decimals-input": Boolean(data?.[index]?.decimalsFixed),
					[classes ?? ""]: Boolean(classes),
				})}
				value={show}
				InputProps={{
					readOnly: inputDisabled || isDashed || readOnly,
					startAdornment: (
						<StartAdornment
							{...data?.[index]}
							type={definition.type}
							clickable={isSwitchable}
							disabled={inputDisabled || Boolean(inputDefaultValue)}
							error={inputError(error, variant)}
							hideButton={hideStartButton}
							onSwitchType={handleChangeType}
						/>
					),
					endAdornment: (
						<EndAdornment
							{...data?.[index]}
							value={definition.value}
							type={definition.type}
							isFixed={Boolean(data?.[index]?.decimalsFixed)}
							disabled={inputDisabled}
							error={inputError(error, variant)}
							hideButton={hideEndButton || Boolean(inputDefaultValue)}
						/>
					),
				}}
				disabled={inputDisabled || Boolean(inputDefaultValue) || readOnly}
				error={inputError(error, variant)}
				sx={sx}
				onFocus={() => {
					setEditing(true);
					onClickInput?.();
				}}
				onBlur={handleFocusOut}
				onChange={handleChange}
				onKeyDown={preventKeysBehaviors}
			/>
		</Tooltip>
	);
}
